import { useEffect, useRef, useState } from "react";
import {
    Button, Modal, Select, Spin, message, Progress,
    Tag, Typography, Divider, Alert, Table
} from "antd";
import { GET, POST } from "../../../utils/apiCalls";
import API from "../../../config/api";

const { Text } = Typography;

type Props = {
    open: boolean;
    onClose: () => void;
    onFinish: () => void;
    eventId: number;
    investorIds: number[];
    selectAllEvent: boolean;
};

function BulkEmail({
    open, onClose, onFinish, eventId, investorIds, selectAllEvent
}: Props) {
    const idempotencyKey = useRef<string | null>(null);
    const [templates, setTemplates] = useState<any[]>([]);
    const [templateId, setTemplateId] = useState<number | undefined>();
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [emails, setEmails] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [step, setStep] = useState<"setup" | "preview" | "progress">("setup");
    const [batchId, setBatchId] = useState<number | null>(null);
    const [batch, setBatch] = useState<any>(null);

    useEffect(() => {
        if (!open || !eventId) return;

        setStep("setup");
        setTemplateId(undefined);
        setEmails([]);
        setCurrentIndex(0);
        setBatchId(null);
        setBatch(null);
        getTemplates();
    }, [open, eventId]);

    useEffect(() => {
        if (!open || !batchId || step !== "progress") return;

        let active = true;
        let timer: ReturnType<typeof setTimeout>;

        const getProgress = async () => {
            try {
                const res: any = await GET(
                    `${API.BULK_EMAIL_BATCH}${batchId}`,
                    null
                );

                if (!active) return;

                if (res?.status) {
                    setBatch(res.data);

                    if (res.data.status === "completed") return;
                }

                timer = setTimeout(getProgress, 3000);
            } catch (error) {
                if (active) timer = setTimeout(getProgress, 5000);
            }
        };

        getProgress();

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [open, batchId, step]);

    const getTemplates = async () => {
        try {
            setLoadingTemplates(true);

            const res: any = await GET(
                API.MAIL_TEMPLATE_LIST + eventId,
                null
            );

            if (res?.status) {
                const records = Array.isArray(res.data)
                    ? res.data
                    : res.data?.rows || res.data?.data || [];
                console.table(records.map((item: any) => ({
                    id: item.id,
                    name: item.name,
                    subject: item.subject,
                    type: item.type,
                    eventId: item.eventId,
                    is_deleted: item.is_deleted
                })));
                const meetingTemplates = records
                    .filter((item: any) =>
                        item.type === "meeting" &&
                        Number(item.eventId) === Number(eventId) &&
                        !item.is_deleted
                    )
                    .sort((a: any, b: any) =>
                        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                    );

                setTemplates(meetingTemplates.slice(0, 1));
            } else {
                message.error("Failed to load email templates");
            }
        } catch (error) {
            message.error("Unable to load email templates");
        } finally {
            setLoadingTemplates(false);
        }
    };

    const generatePreview = async () => {
        if (!templateId) {
            message.warning("Please select an email template");
            return;
        }

        try {
            setPreviewLoading(true);

            const payload: any = {
                eventId: Number(eventId),
                templateId: Number(templateId)
            };

            if (!selectAllEvent) {
                payload.investorIds = investorIds;
            }

            const res: any = await POST(
                API.BULK_EMAIL_PREVIEW,
                payload
            );

            if (res?.status && res.data?.emails?.length) {
                idempotencyKey.current = crypto.randomUUID();
                setEmails(res.data.emails);
                setCurrentIndex(0);
                setStep("preview");
            } else {
                message.error("No email previews available");
            }
        } catch (error: any) {
            message.error(
                error?.response?.data?.message ||
                "Failed to generate email previews"
            );
        } finally {
            setPreviewLoading(false);
        }
    };

    const queueEmails = async () => {
        if (sending || !emails.length) return;

        try {
            setSending(true);

            // Use the exact CC IDs generated during preview.
            // This also supports "Select All Event CCs".
            const ids = emails.map((item: any) =>
                Number(item.investor_id)
            );

            const res: any = await POST(
                API.BULK_EMAIL_SEND,
                {
                    eventId: Number(eventId),
                    templateId: Number(templateId),
                    investorIds: ids,
                    idempotencyKey: idempotencyKey.current
                }
            );

            if (res?.status && res.data?.batchId) {
                setBatchId(res.data.batchId);
                setStep("progress");
                message.success("Emails added to sending queue");
            } else {
                message.error("Unable to queue emails");
            }
        } catch (error: any) {
            message.error(
                error?.response?.data?.message ||
                "Failed to queue emails"
            );
        } finally {
            setSending(false);
        }
    };

    const currentEmail = emails[currentIndex];
    const total = Number(batch?.total_count || 0);
    const sent = Number(batch?.sent_count || 0);
    const failed = Number(batch?.failed_count || 0);
    const processed = sent + failed;
    const percent = total ? Math.round((processed / total) * 100) : 0;
    const completed = batch?.status === "completed";

    const closeModal = () => {
        if (sending) return;
        if (batchId) onFinish();
        else onClose();
    };

    return (
        <Modal
            title="Bulk Email - Corporates"
            open={open}
            onCancel={closeModal}
            width={1000}
            centered
            destroyOnClose
            maskClosable={false}
            footer={
                step === "setup" ? (
                    <>
                        <Button onClick={closeModal}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={previewLoading}
                            disabled={!templateId}
                            onClick={generatePreview}
                        >
                            Generate Preview
                        </Button>
                    </>
                ) : step === "preview" ? (
                    <>
                        <Button onClick={() => setStep("setup")}>
                            Back
                        </Button>
                        <Button
                            onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
                            disabled={currentIndex === 0}
                        >
                            Previous
                        </Button>
                        <Button
                            onClick={() => setCurrentIndex(i =>
                                Math.min(emails.length - 1, i + 1)
                            )}
                            disabled={currentIndex === emails.length - 1}
                        >
                            Next
                        </Button>
                        <Button
                            type="primary"
                            loading={sending}
                            onClick={() => {
                                Modal.confirm({
                                    title: "Confirm Bulk Email",
                                    content: `Send ${emails.length} personalized emails? This action cannot be undone.`,
                                    okText: "Confirm & Send",
                                    okButtonProps: { danger: true },
                                    onOk: queueEmails
                                });
                            }}
                        >
                            Confirm & Send ({emails.length})
                        </Button>
                    </>
                ) : (
                    <Button onClick={closeModal}>
                        {completed ? "Done" : "Close"}
                    </Button>
                )
            }
        >
            {step === "setup" && (
                <div style={{ padding: "12px 0" }}>
                    <div style={{ marginBottom: 20 }}>
                        <Text strong>Recipients</Text>
                        <div style={{ marginTop: 8 }}>
                            <Tag color="blue">
                                {selectAllEvent
                                    ? "All CCs in selected event"
                                    : `${investorIds.length} selected CCs`}
                            </Tag>
                        </div>
                    </div>

                    <div style={{ marginBottom: 8 }}>
                        <Text strong>Email Template</Text>
                    </div>

                    <Select
                        style={{ width: "100%" }}
                        size="large"
                        placeholder="Select meeting email template"
                        loading={loadingTemplates}
                        value={templateId}
                        onChange={(value) => setTemplateId(value)}
                        options={templates.map((item: any) => ({
                            label: `${item.subject || item.name} (ID: ${item.id})`,
                            value: Number(item.id)
                        }))}
                    />

                    <Alert
                        style={{ marginTop: 20 }}
                        type="info"
                        showIcon
                        message="Each CC will receive a personalized email containing their own meeting details."
                        description="Generating previews will not send any emails."
                    />
                </div>
            )}

            {step === "preview" && currentEmail && (
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Text strong>Personalized Email Preview</Text>
                        <Tag color="blue">
                            {currentIndex + 1} of {emails.length}
                        </Tag>
                    </div>

                    <div style={{
                        border: "1px solid #e8e8e8",
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 16
                    }}>
                        <div className="mb-2">
                            <Text type="secondary">To: </Text>
                            <Text strong>
                                {currentEmail.investor_name} &lt;{currentEmail.recipient_email}&gt;
                            </Text>
                        </div>

                        <div>
                            <Text type="secondary">Subject: </Text>
                            <Text strong>{currentEmail.subject}</Text>
                        </div>
                    </div>

                    <iframe
                        title={`Email Preview ${currentIndex + 1}`}
                        sandbox=""
                        srcDoc={currentEmail.html_content || ""}
                        style={{
                            width: "100%",
                            height: 420,
                            border: "1px solid #e8e8e8",
                            borderRadius: 8,
                            background: "#fff"
                        }}
                    />
                </div>
            )}

            {step === "progress" && (
                <div>
                    <div className="mb-3">
                        <Text strong>Batch #{batchId}</Text>
                        <div>
                            <Tag color={completed ? "green" : "processing"}>
                                {completed ? "Completed" : "Processing"}
                            </Tag>
                        </div>
                    </div>

                    <Progress
                        percent={percent}
                        status={completed && failed ? "exception" : "active"}
                    />

                    <div className="row text-center my-4">
                        <div className="col-4">
                            <h3>{total}</h3>
                            <Text type="secondary">Total</Text>
                        </div>
                        <div className="col-4">
                            <h3 style={{ color: "#52c41a" }}>{sent}</h3>
                            <Text type="secondary">Sent</Text>
                        </div>
                        <div className="col-4">
                            <h3 style={{ color: failed ? "#ff4d4f" : undefined }}>
                                {failed}
                            </h3>
                            <Text type="secondary">Failed</Text>
                        </div>
                    </div>

                    <Divider />

                    {batch?.jobs?.length > 0 && (
                        <Table
                            size="small"
                            rowKey="id"
                            dataSource={batch.jobs}
                            pagination={{ pageSize: 10 }}
                            columns={[
                                {
                                    title: "Recipient",
                                    dataIndex: "recipient_email"
                                },
                                {
                                    title: "Status",
                                    dataIndex: "status",
                                    render: (status: string) => (
                                        <Tag color={
                                            status === "sent" ? "green" :
                                                status === "failed" ? "red" :
                                                    status === "processing" ? "blue" : "default"
                                        }>
                                            {status}
                                        </Tag>
                                    )
                                },
                                {
                                    title: "Attempts",
                                    dataIndex: "attempts"
                                },
                                {
                                    title: "Error",
                                    dataIndex: "error_message",
                                    ellipsis: true
                                }
                            ]}
                        />
                    )}

                    {completed && (
                        <Alert
                            type={failed ? "warning" : "success"}
                            showIcon
                            message={
                                failed
                                    ? `Completed with ${failed} failed emails`
                                    : "All emails sent successfully"
                            }
                        />
                    )}
                </div>
            )}
        </Modal>
    );
}

export default BulkEmail;